import React, { createContext, useContext, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        // First check if theme is already applied to DOM (from index.html script)
        const root = document.documentElement;
        if (root.classList.contains('dark')) {
            return 'dark';
        }
        if (root.classList.contains('light')) {
            return 'light';
        }

        // Check localStorage
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            return savedTheme;
        }

        // Check system preference
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    });

    useEffect(() => {
        // Sync theme class with state (handles initial load and external changes)
        const root = document.documentElement;
        const currentTheme = root.classList.contains('dark') ? 'dark' :
            root.classList.contains('light') ? 'light' : null;

        // Only update DOM if theme changed (skip if already applied by toggleTheme)
        if (currentTheme !== theme) {
            root.classList.remove('light', 'dark');
            root.classList.add(theme);
        }

        // Always save to localStorage to keep it in sync
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        // Apply theme change immediately to DOM for instant visual feedback
        const root = document.documentElement;
        const newTheme = theme === 'light' ? 'dark' : 'light';

        // Temporarily disable all transitions for instant theme change
        root.classList.add('no-transitions');

        // Remove both classes and add the new one immediately
        root.classList.remove('light', 'dark');
        root.classList.add(newTheme);

        // Force synchronous state update so all components update together
        flushSync(() => {
            setTheme(newTheme);
        });

        // Re-enable transitions after browser has painted the new theme
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                root.classList.remove('no-transitions');
            });
        });
    };

    const value = {
        theme,
        toggleTheme,
        isDark: theme === 'dark',
        isLight: theme === 'light'
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
